import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Box,
    Collapse,
    Fade,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useState } from 'react';
import MatterFieldInput from '../MatterFieldInput';
import type { ExtraExtractedField, ExtraExtractedFieldGroup } from './extractionDisplay';

interface Props {
    groups: ExtraExtractedFieldGroup[];
    visible: boolean;
    delayMs: number;
    /** Current values keyed by matter field key, including edits made elsewhere in the modal. */
    values: Record<string, string>;
    onFieldChange(key: string, value: string): void;
}

interface GroupTileProps {
    group: ExtraExtractedFieldGroup;
    wideTile?: boolean;
    values: Record<string, string>;
    onFieldChange(key: string, value: string): void;
}

const COMPACT_FIELD_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 26,
        bgcolor: '#fff',
    },
    '& .MuiOutlinedInput-input': {
        py: '3px',
        px: 0.75,
        fontSize: 12,
        height: 'auto',
    },
    '& .MuiSelect-select': {
        py: '3px',
        px: 0.75,
        fontSize: 12,
    },
    '& .MuiInputAdornment-root': {
        mr: 0,
        ml: 0,
    },
    '& .MuiInputAdornment-positionStart': {
        pl: 0.75,
    },
    '& .MuiIconButton-root': {
        p: 0.25,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        fontSize: 16,
    },
    '& .MuiAutocomplete-root .MuiTextField-root': {
        mt: 0,
    },
    '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
        p: 0,
    },
    '& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
        py: '3px',
        px: 0.75,
    },
    '& .MuiFormHelperText-root': {
        display: 'none',
    },
};

function isFilledValue(value: string): boolean {
    return value.trim().length > 0;
}

function selectOptionsForValue(field: ExtraExtractedField, value: string) {
    const options = field.options ?? [];
    if (!value || options.some((option) => option.value === value)) {
        return options;
    }

    return [...options, { value, label: value }];
}

function ExtractedFieldInput({
    field,
    value,
    onChange,
}: {
    field: ExtraExtractedField;
    value: string;
    onChange(value: string): void;
}) {
    if (field.options) {
        return (
            <TextField
                select
                size="small"
                hiddenLabel
                fullWidth
                value={value}
                onChange={(event) => onChange(event.target.value)}
                SelectProps={{ displayEmpty: true }}
                sx={COMPACT_FIELD_SX}
            >
                <MenuItem value="">
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                        —
                    </Box>
                </MenuItem>
                {selectOptionsForValue(field, value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (field.widget !== 'text') {
        return (
            <Box sx={COMPACT_FIELD_SX}>
                <MatterFieldInput
                    name={field.key}
                    widget={field.widget}
                    value={value}
                    size="small"
                    onChange={onChange}
                />
            </Box>
        );
    }

    return (
        <TextField
            size="small"
            hiddenLabel
            fullWidth
            placeholder="—"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onFocus={(event) => event.target.select()}
            inputProps={{
                spellCheck: false,
                autoComplete: 'off',
            }}
            sx={COMPACT_FIELD_SX}
        />
    );
}

function GroupTile({ group, wideTile = false, values, onFieldChange }: GroupTileProps) {
    const theme = useTheme();
    const extractedInGroup = group.fields.filter((field) => isFilledValue(field.value)).length;
    const hasExtracted = extractedInGroup > 0;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: '6px',
                border: `1px solid ${hasExtracted ? '#bbdefb' : '#e8eaed'}`,
                bgcolor: '#fff',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                    px: 1,
                    py: 0.5,
                    borderBottom: `1px solid ${hasExtracted ? '#bbdefb' : '#e8eaed'}`,
                    background: hasExtracted
                        ? 'linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)'
                        : '#f8f9fa',
                }}
            >
                <Typography
                    variant="caption"
                    noWrap
                    title={group.title}
                    sx={{ fontWeight: 600, color: '#202124', lineHeight: 1.2 }}
                >
                    {group.title}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        flexShrink: 0,
                        color: theme.palette.text.secondary,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.2,
                    }}
                >
                    {extractedInGroup}/{group.fields.length}
                </Typography>
            </Stack>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${wideTile ? 3 : 2}, minmax(0, 1fr))`,
                    gap: 0.5,
                    p: 0.75,
                    alignContent: 'start',
                }}
            >
                {group.fields.map((field) => (
                    <Stack
                        key={field.key}
                        spacing={0.125}
                        sx={{ minWidth: 0, gridColumn: field.wide ? '1 / -1' : undefined }}
                    >
                        <Typography
                            variant="caption"
                            noWrap
                            title={field.label}
                            sx={{
                                fontSize: 10,
                                lineHeight: 1.2,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {field.label}
                        </Typography>
                        <ExtractedFieldInput
                            field={field}
                            value={values[field.key] ?? ''}
                            onChange={(value) => onFieldChange(field.key, value)}
                        />
                    </Stack>
                ))}
            </Box>
        </Box>
    );
}

export default function ExtraExtractedProperties({
    groups,
    visible,
    delayMs,
    values,
    onFieldChange,
}: Props) {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(true);

    if (groups.length === 0) {
        return null;
    }

    const allFields = groups.flatMap((group) => group.fields);
    const filledCount = allFields.filter((field) => isFilledValue(field.value)).length;
    const countPhrase = filledCount === 1 ? '1 field' : `${filledCount} fields`;
    const remainder = filledCount === 1 ? ' was extracted!' : ' were extracted!';

    return (
        <Fade
            in={visible}
            timeout={500}
            style={{ transitionDelay: visible ? `${delayMs}ms` : '0ms' }}
            unmountOnExit
        >
            <Stack spacing={1} sx={{ width: '100%' }} alignItems="center">
                <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded((current) => !current)}
                    aria-expanded={expanded}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: theme.palette.text.secondary,
                        font: 'inherit',
                        '&:hover': {
                            color: theme.palette.primary.main,
                            '& .MuiTypography-root, & .MuiSvgIcon-root': {
                                color: theme.palette.primary.main,
                            },
                        },
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        {filledCount > 0 ? (
                            <>
                                <Box
                                    component="span"
                                    sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
                                >
                                    {countPhrase}
                                </Box>
                                {remainder}
                            </>
                        ) : (
                            'Review additional fields'
                        )}
                    </Typography>
                    <ExpandMoreIcon
                        sx={{
                            fontSize: 24,
                            color: 'inherit',
                            transition: 'transform 0.3s ease',
                            transform: expanded ? 'rotate(180deg)' : undefined,
                        }}
                    />
                </Box>
                <Collapse in={expanded} unmountOnExit sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                md: 'repeat(3, minmax(0, 1fr))',
                                lg: 'repeat(4, minmax(0, 1fr))',
                            },
                            gap: 1,
                            pt: 0.5,
                            alignItems: 'stretch',
                        }}
                    >
                        {groups.map((group) => {
                            const wideTile = group.fields.length >= 10;

                            return (
                                <Box
                                    key={group.title}
                                    sx={{
                                        minWidth: 0,
                                        display: 'flex',
                                        gridColumn: {
                                            md: wideTile ? 'span 2' : 'span 1',
                                        },
                                    }}
                                >
                                    <GroupTile
                                        group={group}
                                        wideTile={wideTile}
                                        values={values}
                                        onFieldChange={onFieldChange}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Collapse>
            </Stack>
        </Fade>
    );
}
